
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import searchIcon from "../image/search.png";
import logo from "../image/logo.png";
import atendimento from "../image/atendimento.png";
import cart from "../image/cart.png";
import favorito from "../image/favorito.png";
import { useCart } from "../context/CartContext";
import { useFavorites } from "../context/FavoriteContext";
import { useSearchQuery } from "../hooks";
import { getProductUrl } from "../utils/productUrl";
import {
  getResponsiveImageProps,
  THUMB_SRCSET_WIDTHS,
} from "../utils/cloudinaryImage";
import UserMenu from "./UserMenu";
import { getCachedWhatsAppNumber } from "../services/siteContactApi";
import { buildWhatsAppChatUrl } from "../utils/whatsapp";
import "../styles/Header.css";

const HEADER_WHATSAPP_MESSAGE =
  "Olá, Emperc! 👋\n\nGostaria de atendimento sobre peças e componentes.";

const Header = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    
    const navigate = useNavigate();
    const { cartItems } = useCart();
    const { favoritesCount } = useFavorites();
    
    // Hook otimizado com React Query para busca
    const { suggestions, suggestionProducts } = useSearchQuery(searchTerm, {
        minLength: 2,
        debounceDelay: 300,
        suggestionsOnly: true, // Apenas sugestões no header
    });

    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = cartItems.reduce(
        (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
        0
    );

    const handleLogoClick = () => {
        navigate("/", { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearch = () => {
        if (searchTerm.trim() !== "") {
            setShowSuggestions(false);
            navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
        }
    };

    // Mostra sugestões quando há resultados
    React.useEffect(() => {
        if (suggestions.length > 0 || suggestionProducts.length > 0) {
            setShowSuggestions(true);
        } else if (searchTerm.trim().length < 2) {
            setShowSuggestions(false);
        }
    }, [suggestions, suggestionProducts, searchTerm]);

    const handleSuggestionClick = (suggestion) => {
        setSearchTerm(suggestion);
        setShowSuggestions(false);
        navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    };

    const handleProductSuggestionClick = (product) => {
        setSearchTerm(product.name);
        setShowSuggestions(false);
        navigate(getProductUrl(product));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="viewport" content="width=device-width, initial-scale=1" />
            </Helmet>
            <div>
                <div className="notification-bar">
                    10% de desconto no pagamento via PIX
                </div>

            <header className="main-header">
                <div className="header-container">
                    <div className="header-top">
                        {/* ✅ Logo - À esquerda (no lugar do hamburger) */}
                        <div className="logo-wrapper" onClick={handleLogoClick}>
                            <img src={logo} alt="Emperc Logo" width="200" height="45" loading="eager" />
                        </div>

                        {/* ✅ Busca - No centro */}
                        <div className="search-wrapper">
                            <div className="search-bar">
                                <input
                                    type="text"
                                    placeholder="Ex.: UN55J5500 ou BN91-12904"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setShowSuggestions(true);
                                    }}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => {
                                        if (suggestions.length > 0 || suggestionProducts.length > 0) {
                                            setShowSuggestions(true);
                                        }
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => setShowSuggestions(false), 200);
                                    }}
                                    aria-label="Buscar por modelo de TV ou código da placa"
                                />

                                <button
                                    type="button"
                                    onClick={handleSearch}
                                    className="search-icon-button"
                                    aria-label="Buscar"
                                >
                                    <img
                                        src={searchIcon}
                                        alt="Search"
                                        className="search-icon"
                                        width="30"
                                        height="30"
                                    />
                                </button>

                                {/* ✅ Dropdown de sugestões */}
                                {showSuggestions && (suggestions.length > 0 || suggestionProducts.length > 0) && (
                                    <div className="search-suggestions" style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                        zIndex: 1000,
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                        marginTop: '4px'
                                    }}>
                                        {/* Produtos sugeridos */}
                                        {suggestionProducts.length > 0 && (
                                            <div style={{ padding: '8px' }}>
                                                <div style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: 'bold', 
                                                    color: '#666',
                                                    marginBottom: '8px',
                                                    padding: '4px 8px'
                                                }}>
                                                    Produtos
                                                </div>
                                                {suggestionProducts.map((product) => {
                                                    const thumb = product.image
                                                        ? getResponsiveImageProps(product.image, {
                                                            widths: THUMB_SRCSET_WIDTHS,
                                                            fallbackWidth: 80,
                                                        })
                                                        : null;
                                                    return (
                                                    <div
                                                        key={product._id}
                                                        onClick={() => handleProductSuggestionClick(product)}
                                                        style={{
                                                            padding: '8px 12px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px',
                                                            borderBottom: '1px solid #f0f0f0',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                    >
                                                        {thumb?.src ? (
                                                                <img
                                                                    src={thumb.src}
                                                                    srcSet={thumb.srcSet}
                                                                    sizes="40px"
                                                                    alt={product.name}
                                                                    width={40}
                                                                    height={40}
                                                                    loading="lazy"
                                                                    decoding="async"
                                                                    fetchPriority="low"
                                                                    style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }}
                                                                />
                                                        ) : null}
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: '14px', fontWeight: '500' }}>
                                                                {product.name}
                                                            </div>
                                                            {product.brand && (
                                                                <div style={{ fontSize: '12px', color: '#666' }}>
                                                                    {product.brand}
                                                                </div>
                                                            )}
                                                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2c5aa0' }}>
                                                                R$ {Number(product.price).toFixed(2)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Sugestões de texto */}
                                        {suggestions.length > 0 && (
                                            <div style={{ padding: '8px', borderTop: suggestionProducts.length > 0 ? '1px solid #ddd' : 'none' }}>
                                                <div style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: 'bold', 
                                                    color: '#666',
                                                    marginBottom: '8px',
                                                    padding: '4px 8px'
                                                }}>
                                                    Sugestões
                                                </div>
                                                {suggestions.map((suggestion, index) => (
                                                    <div
                                                        key={index}
                                                        onClick={() => handleSuggestionClick(suggestion)}
                                                        style={{
                                                            padding: '8px 12px',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                    >
                                                        🔍 {suggestion}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <p className="search-hint">
                                Busque pelo modelo da TV ou código da placa (ex.: BN91-)
                            </p>
                        </div>

                        {/* ✅ Ações - Ícones à direita */}
                        <div className="header-actions">
                            <a
                              href={buildWhatsAppChatUrl(
                                getCachedWhatsAppNumber(),
                                HEADER_WHATSAPP_MESSAGE
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="header-action-btn"
                              aria-label="Atendimento via WhatsApp"
                            >
                                <img src={atendimento} alt="" aria-hidden="true" width="36" height="36" />
                                <span className="header-action-text">Atendimento</span>
                            </a>

                            <UserMenu />

                            {/* Favoritos (conceito único) */}
                            <a 
                                href="/favoritos" 
                                className="header-action-btn header-favorites-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate("/favoritos");
                                }}
                                aria-label="Favoritos"
                                title="Favoritos — placas salvas"
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        navigate("/favoritos");
                                    }
                                }}
                            >
                                <img src={favorito} alt="" aria-hidden="true" width="36" height="36" />
                                <span className="header-action-text">
                                    Favoritos
                                    {favoritesCount > 0 && (
                                        <>
                                            <br />
                                            {favoritesCount} {favoritesCount === 1 ? 'item' : 'itens'}
                                        </>
                                    )}
                                </span>
                            </a>

                            <a 
                                href="/cart" 
                                className="header-action-btn header-cart-btn" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate("/cart");
                                }}
                                aria-label={`Carrinho de compras${totalItems > 0 ? ` com ${totalItems} ${totalItems === 1 ? 'item' : 'itens'}` : ' vazio'}`}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        navigate("/cart");
                                    }
                                }}
                            >
                                {totalItems > 0 && (
                                    <span className="cart-badge" aria-label={`${totalItems} ${totalItems === 1 ? 'item' : 'itens'} no carrinho`}>
                                        {totalItems}
                                    </span>
                                )}
                                <img src={cart} alt="" aria-hidden="true" width="36" height="36" />
                                <span className="header-action-text">
                                    Carrinho
                                    {totalItems > 0 && (
                                        <>
                                            <br />
                                            {totalItems} {totalItems === 1 ? 'item' : 'itens'} | R$ {totalPrice.toFixed(2)}
                                        </>
                                    )}
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
            </header>
        </div>
        </>
    );
};

export default Header;
